import { TextField, Autocomplete as MUIAutocomplete } from '@mui/material';
import { unionBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Field, useField, FieldRenderProps } from 'react-final-form';

import { showErrorOnBlurOrDirty } from '../text-field';
import { getAutocompleteValue, getOptionLabel, options, Option } from './options';

type RenderInputProps = Omit<FieldRenderProps<unknown>, 'onChange'>;

type StatesAutocompleteProps = {
  name: string;
  label: string;
  disabled?: boolean;
  fullWidth?: boolean;
};

export const StatesAutocomplete = ({ name, label, disabled, fullWidth }: StatesAutocompleteProps) => {
  const stateField = useField(name);

  const getOptionSelected = useCallback(
    (option: Option, currentValue: Option) => currentValue !== null && option.value === currentValue?.value,
    [],
  );

  const autocompleteValue: Option | null = useMemo(
    () => (stateField.input.value ? getAutocompleteValue(stateField.input.value) : null),
    [stateField.input.value],
  );

  const autocompleteOptions: Option[] = useMemo(
    () => (autocompleteValue ? unionBy(options, [autocompleteValue], 'value') : options),
    [autocompleteValue],
  );

  const renderInput = useCallback(
    ({ input, meta }: RenderInputProps) => {
      const isError = showErrorOnBlurOrDirty({ meta });

      return (
        <MUIAutocomplete
          {...input}
          value={autocompleteValue}
          fullWidth={fullWidth}
          disablePortal
          options={autocompleteOptions}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={getOptionSelected}
          disabled={disabled}
          onChange={(_, option: Option | Option[] | null) => {
            if (Array.isArray(option)) {
              input.onChange(option[0]?.value || null);
            } else {
              input.onChange(option?.value || null);
            }
          }}
          renderInput={(params) => {
            return <TextField {...params} error={isError} helperText={isError ? meta.error : ''} label={label} />;
          }}
        />
      );
    },
    [autocompleteValue, disabled, fullWidth, getOptionSelected, autocompleteOptions, label],
  );

  return <Field name={name} render={renderInput} />;
};
